import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	SxProps,
	Typography,
	styled,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { combineSX } from '@/utils/combineSX';
import React from 'react';

const CustomAccordion = styled(Accordion)({
	borderBottom: '1px solid rgba(0, 0, 0, .125)',
});

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
	fontSize: 'var(--font-size-default)',
	fontWeight: 'bold',
	[theme.breakpoints.down('md')]: {
		fontSize: 'var(--font-size-caption)',
	},
}));

export const DpAccordionFrame = ({
	children,
	accordionsx,
	defaultexpanded,
	islast,
	...props
}: {
	accordionsx?: SxProps;
	children: React.ReactNode;
	defaultexpanded?: boolean;
	islast?: boolean;
}) => {
	const cleanChildren = React.Children.toArray(children).filter((child) =>
		React.isValidElement(child)
	);

	return (
		<CustomAccordion
			disableGutters
			elevation={0}
			square={false}
			defaultExpanded={defaultexpanded}
			{...props}
			sx={combineSX([
				{
					borderBottomLeftRadius: '0px !important',
					borderBottomRightRadius: '0px !important',
					borderBottom: islast ? 'none' : '1px solid #E2E2E2',
				},
				...(Array.isArray(accordionsx) ? accordionsx : [accordionsx]),
			])}
		>
			{cleanChildren}
		</CustomAccordion>
	);
};

export const DpAccordionSummary = ({
	id,
	children,
	summarysx,
	isfirst,
	componenttitle = 'h3',
	nouppercase,
}: {
	id: string;
	summarysx?: SxProps;
	children: React.ReactNode;
	isfirst?: boolean;
	componenttitle?: string;
	nouppercase?: boolean;
}) => (
	<CustomAccordionSummary
		expandIcon={<ExpandMoreIcon />}
		id={id}
		aria-controls={id}
		sx={combineSX([
			{
				borderTop: isfirst ? '1px solid #E2E2E2' : 'none',
			},
			...(Array.isArray(summarysx) ? summarysx : [summarysx]),
		])}
	>
		<Typography
			component={componenttitle as React.ElementType}
			sx={combineSX([
				{
					fontSize: { xs: 'var(--font-size-default)', md: 'var(--font-size-sm)' },
					fontWeight: '600',
					pt: { xs: '15px', sm: '21px' },
					pb: { xs: '15px', sm: '21.5px' },
					color: '#2a2a2a',
					textTransform: nouppercase ? '' : 'uppercase',
				},
				...(Array.isArray(summarysx) ? summarysx : [summarysx]),
			])}
		>
			{children}
		</Typography>
	</CustomAccordionSummary>
);

export const DpAccordionDetails = ({
	children,
	detailsx,
}: {
	children: React.ReactNode;
	detailsx?: SxProps;
}) => (
	<AccordionDetails
		sx={combineSX([{ paddingLeft: '0px', fontSize: 'var(--font-size-body) !important' }, detailsx])}
	>
		{children}
	</AccordionDetails>
);

export const DpAccordion = ({
	id,
	summary,
	children,
	accordionsx,
	summarysx,
	detailsx,
	defaultexpanded,
	isfirst,
	islast,
	componenttitle = 'h3',
	nouppercase,
	...props
}: {
	id: string;
	summary: string;
	accordionsx?: SxProps;
	summarysx?: SxProps;
	detailsx?: SxProps;
	children: React.ReactNode;
	defaultexpanded?: boolean;
	isfirst?: boolean;
	islast?: boolean;
	componenttitle?: string;
	nouppercase?: boolean;
}) => (
	<DpAccordionFrame
		accordionsx={accordionsx}
		defaultexpanded={defaultexpanded}
		islast={islast}
		{...props}
	>
		<DpAccordionSummary
			id={id}
			summarysx={summarysx}
			isfirst={isfirst}
			componenttitle={componenttitle}
			nouppercase={nouppercase}
		>
			{summary}
		</DpAccordionSummary>
		<DpAccordionDetails detailsx={detailsx}>{children}</DpAccordionDetails>
	</DpAccordionFrame>
);
